import React, { useState, useEffect, useCallback } from "react";
import {
  FaCalendarAlt, FaChevronLeft, FaChevronRight, FaClock,
  FaLayerGroup, FaCheckCircle, FaHourglassHalf, FaUserTie, FaTimes,
} from "react-icons/fa";
import { useTheme, getT, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import API_BASE from "./apiConfig";
import { useSort } from "./hooks/useSort";
import { useDateTimeFormat } from "./utils/formatters";

const BASE = API_BASE;
const BLUR = "blur(18px)";
const FONT = "'Segoe UI', system-ui, -apple-system, sans-serif";
const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

// Stable colour per coach name (hash → palette)
const COACH_COLORS = [
  ["#6366f1","rgba(99,102,241,0.15)"],
  ["#0ea5e9","rgba(14,165,233,0.15)"],
  ["#10b981","rgba(16,185,129,0.15)"],
  ["#f59e0b","rgba(245,158,11,0.15)"],
  ["#ec4899","rgba(236,72,153,0.15)"],
  ["#8b5cf6","rgba(139,92,246,0.15)"],
  ["#ef4444","rgba(239,68,68,0.15)"],
  ["#14b8a6","rgba(20,184,166,0.15)"],
];
function coachColor(id) { return COACH_COLORS[(id || 0) % COACH_COLORS.length]; }

function makeS(T) {
  return {
    card: { background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR, border: T.glassBdr, borderRadius: 18, boxShadow: T.glassSh, padding: "24px 28px", boxSizing: "border-box" },
    gBtn: { padding: "7px 14px", border: `1.5px solid ${T.inputBdr}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", color: T.textLabel, background: T.glass, display: "flex", alignItems: "center", gap: 5, fontFamily: FONT },
  };
}

// ─── Hours Gauge ──────────────────────────────────────────────────────────────
function HoursGauge({ used, max, T }) {
  const pct = max > 0 ? Math.min(100, (used / max) * 100) : 0;
  const color = pct >= 90 ? "#ef4444" : pct >= 70 ? "#f59e0b" : "#10b981";
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700, color: T.textLabel, fontFamily: FONT, marginBottom: 6 }}>
        <span>Hours Used</span><span style={{ color }}>{used}h / {max}h</span>
      </div>
      <div style={{ height: 10, borderRadius: 6, background: T.divider, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 6, transition: "width 0.4s" }} />
      </div>
      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4, fontFamily: FONT }}>
        {max - used > 0 ? `${max - used}h remaining` : "No hours remaining"}
      </div>
    </div>
  );
}

// ─── Day Detail Panel (shown below calendar when a day is clicked) ────────────
function DayDetail({ dateStr, sessions, allCoaches, showAll, planStatus, T, onClose }) {
  const d = new Date(dateStr + "T00:00:00");
  const label = d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const items = sessions.filter(s => s.assign_date === dateStr);
  const { sorted: sortedItems, toggleSort: toggleItemsSort, sortArrow: itemsArrow } = useSort(items, null);

  // When showing all coaches, group by coach
  const byCoach = {};
  for (const s of items) {
    const key = s.coach_name || s.batch_name;
    byCoach[key] = byCoach[key] || { coach_name: s.coach_name, coach_id: s.coach_id, batches: [] };
    byCoach[key].batches.push(s);
  }

  return (
    <div style={{
      marginTop: 12, border: `1.5px solid ${T.yellow}`, borderRadius: 14,
      background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR, padding: "18px 20px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: FONT }}>{label}</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2, fontFamily: FONT }}>
            {items.length} session{items.length !== 1 ? "s" : ""} scheduled
          </div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, padding: 4 }}>
          <FaTimes />
        </button>
      </div>

      {items.length === 0 ? (
        <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, textAlign: "center", padding: "16px 0" }}>
          No sessions on this day.
        </div>
      ) : showAll ? (
        /* All-coaches view: one row per coach */
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {[["Coach","coach_name"],["Batch","batch_name"],["Type","slot_type"],["Status","display_status"]].map(([h, key]) => (
                <th key={h} onClick={() => toggleItemsSort(key)} style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: T.textMuted, textAlign: "left", padding: "0 12px 8px 0", fontFamily: FONT, cursor: "pointer", userSelect: "none" }}>{h}{itemsArrow(key)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(s => {
              const [fg, bg] = coachColor(s.coach_id);
              const isApproved = planStatus === "approved" || s.display_status === "approved";
              return (
                <tr key={s.id} style={{ borderTop: `1px solid ${T.divider}` }}>
                  <td style={{ padding: "9px 12px 9px 0" }}>
                    <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: bg, color: fg, fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <FaUserTie size={9} />{s.coach_name}
                    </span>
                  </td>
                  <td style={{ padding: "9px 12px 9px 0", fontSize: 13, fontWeight: 600, color: T.text, fontFamily: FONT }}>{s.batch_name}</td>
                  <td style={{ padding: "9px 12px 9px 0" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "rgba(99,102,241,0.12)", color: "#6366f1", fontFamily: FONT, textTransform: "capitalize" }}>{s.slot_type}</span>
                  </td>
                  <td>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, fontFamily: FONT,
                      background: isApproved ? "rgba(16,185,129,0.15)" : "rgba(251,191,36,0.15)",
                      color: isApproved ? getBrandColors().green : getBrandColors().orange }}>
                      {isApproved ? "✓ Approved" : "Draft"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        /* Own sessions view */
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map(s => {
            const isApproved = planStatus === "approved" || s.display_status === "approved";
            return (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 10,
                background: isApproved ? "rgba(16,185,129,0.07)" : "rgba(99,102,241,0.07)",
                border: `1px solid ${isApproved ? "rgba(16,185,129,0.2)" : "rgba(99,102,241,0.2)"}` }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: FONT }}>{s.batch_name}</div>
                  {s.level_name && (
                    <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>{s.level_name}</div>
                  )}
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, fontFamily: FONT,
                  background: "rgba(99,102,241,0.12)", color: "#6366f1", textTransform: "capitalize" }}>{s.slot_type}</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, fontFamily: FONT,
                  background: isApproved ? "rgba(16,185,129,0.15)" : "rgba(251,191,36,0.15)",
                  color: isApproved ? getBrandColors().green : getBrandColors().orange }}>
                  {isApproved ? "✓ Approved" : "Draft"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Calendar Grid ────────────────────────────────────────────────────────────
function ScheduleCalendar({ month, year, sessions, showAll, planStatus, T }) {
  const [selectedDate, setSelectedDate] = useState(null);

  const byDate = {};
  for (const s of sessions) {
    byDate[s.assign_date] = byDate[s.assign_date] || [];
    byDate[s.assign_date].push(s);
  }

  const firstDay = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const startDow = (firstDay.getDay() + 6) % 7; // Mon=0

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const today = new Date().toISOString().slice(0, 10);

  const handleDayClick = (dateStr, items) => {
    setSelectedDate(prev => prev === dateStr ? null : dateStr);
  };

  // Reset selected date when month/year changes
  useEffect(() => { setSelectedDate(null); }, [month, year]);

  return (
    <div>
      {/* Day headers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4, marginBottom: 4 }}>
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 800, color: T.textMuted, textTransform: "uppercase", fontFamily: FONT }}>{d}</div>
        ))}
      </div>

      {/* Calendar cells */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const dateStr = `${year}-${String(month).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
          const items = byDate[dateStr] || [];
          const isSun = new Date(year, month - 1, day).getDay() === 0;
          const isToday = dateStr === today;
          const isSelected = selectedDate === dateStr;
          const hasItems = items.length > 0;

          return (
            <div
              key={dateStr}
              onClick={() => !isSun && handleDayClick(dateStr, items)}
              style={{
                background: isSun ? "rgba(0,0,0,0.04)" : isSelected ? `rgba(247,206,11,0.12)` : hasItems ? "rgba(99,102,241,0.07)" : T.glass,
                backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
                border: isSelected ? `2px solid ${T.yellow}` : isToday ? `2px solid ${T.yellow}` : T.glassBdr,
                borderRadius: 10, padding: "8px 8px", minHeight: 72,
                opacity: isSun ? 0.35 : 1,
                cursor: isSun ? "default" : "pointer",
                boxSizing: "border-box",
                transition: "background 0.15s",
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 800, color: isToday || isSelected ? T.yellow : T.textLabel, marginBottom: 4, fontFamily: FONT }}>{day}</div>
              {showAll
                ? /* All coaches: colour chip per coach */
                  items.slice(0, 3).map((s, si) => {
                    const [fg, bg] = coachColor(s.coach_id);
                    return (
                      <div key={si} style={{ fontSize: 9, background: bg, color: fg, borderRadius: 4, padding: "1px 5px", marginBottom: 2,
                        fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: FONT }}>
                        {(s.coach_name || "").split(" ")[0]} · {s.batch_name}
                      </div>
                    );
                  })
                : /* Own sessions */
                  items.slice(0, 2).map((s, si) => {
                    const isApproved = planStatus === "approved" || s.display_status === "approved";
                    return (
                      <div key={si} style={{ fontSize: 9, borderRadius: 4, padding: "1px 5px", marginBottom: 2,
                        fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: FONT,
                        background: isApproved ? "rgba(16,185,129,0.18)" : "rgba(99,102,241,0.15)",
                        color:      isApproved ? getBrandColors().green : "#6366f1" }}>
                        {s.batch_name}
                      </div>
                    );
                  })
              }
              {items.length > (showAll ? 3 : 2) && (
                <div style={{ fontSize: 9, color: T.textMuted, fontFamily: FONT }}>+{items.length - (showAll ? 3 : 2)}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Expanded day detail */}
      {selectedDate && (
        <DayDetail
          dateStr={selectedDate}
          sessions={sessions}
          showAll={showAll}
          planStatus={planStatus}
          T={T}
          onClose={() => setSelectedDate(null)}
        />
      )}
    </div>
  );
}

// ─── Session List ─────────────────────────────────────────────────────────────
function SessionList({ sessions, showAll, planStatus, T }) {
  const { sorted: sortedSessions, toggleSort, sortArrow } = useSort(sessions, "assign_date");
  const { fmtDate } = useDateTimeFormat();

  if (!sessions.length) return (
    <div style={{ textAlign: "center", padding: "32px 0", color: T.textMuted, fontSize: 13, fontFamily: FONT }}>
      No sessions scheduled this month.
    </div>
  );

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {[...(showAll ? [["Coach","coach_name"]] : []), ["Date","assign_date"],["Batch","batch_name"],["Level","level_name"],["Type","slot_type"],["Status","display_status"]].map(([h, key]) => (
              <th key={h} onClick={() => toggleSort(key)} style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: T.textMuted, textAlign: "left", padding: "0 12px 10px 0", fontFamily: FONT, cursor: "pointer", userSelect: "none" }}>{h}{sortArrow(key)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedSessions.map(s => {
            const dateLabel = fmtDate(s.assign_date);
            const isApproved = planStatus === "approved" || s.display_status === "approved";
            const [fg, bg] = coachColor(s.coach_id);
            return (
              <tr key={s.id} style={{ borderTop: `1px solid ${T.divider}` }}>
                {showAll && (
                  <td style={{ padding: "9px 12px 9px 0" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: bg, color: fg, fontFamily: FONT }}>
                      {s.coach_name}
                    </span>
                  </td>
                )}
                <td style={{ padding: "9px 12px 9px 0", fontSize: 13, color: T.text, fontFamily: FONT, whiteSpace: "nowrap" }}>{dateLabel}</td>
                <td style={{ padding: "9px 12px 9px 0", fontSize: 13, fontWeight: 600, color: T.text, fontFamily: FONT }}>{s.batch_name}</td>
                <td style={{ padding: "9px 12px 9px 0" }}>
                  {s.level_name && (
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: `${s.color_hex || "#6366f1"}22`, color: s.color_hex || "#6366f1", fontFamily: FONT }}>
                      {s.level_name}
                    </span>
                  )}
                </td>
                <td style={{ padding: "9px 12px 9px 0" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "rgba(99,102,241,0.12)", color: "#6366f1", fontFamily: FONT, textTransform: "capitalize" }}>
                    {s.slot_type}
                  </span>
                </td>
                <td>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, fontFamily: FONT,
                    background: isApproved ? "rgba(16,185,129,0.15)" : "rgba(251,191,36,0.15)",
                    color: isApproved ? getBrandColors().green : getBrandColors().orange }}>
                    {isApproved ? "✓ Approved" : "Draft"}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main CoachScheduleView ────────────────────────────────────────────────────
export default function CoachScheduleView({ currentUser }) {
  const { isDark } = useTheme(); const T = getT(isDark); const S = makeS(T);
  const { fmtDate } = useDateTimeFormat();
  const now = new Date();
  const [month, setMonth]       = useState(now.getMonth() + 1);
  const [year,  setYear]        = useState(now.getFullYear());
  const [data,  setData]        = useState(null);
  const [allData, setAllData]   = useState(null);   // full plan for "show all coaches"
  const [loading, setLoading]   = useState(false);
  const [view, setView]         = useState("calendar");
  const [showAll, setShowAll]   = useState(false);

  const coachId = currentUser?.user_id;

  // Load own schedule
  const load = useCallback(() => {
    if (!coachId) return;
    setLoading(true);
    fetch(`${BASE}/coach-planner/my-schedule?coach_id=${coachId}&month=${month}&year=${year}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [coachId, month, year]);

  // Load full plan (all coaches)
  const loadAll = useCallback(() => {
    fetch(`${BASE}/coach-planner/plan?month=${month}&year=${year}`)
      .then(r => r.json())
      .then(d => setAllData(d))
      .catch(() => {});
  }, [month, year]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showAll) loadAll(); }, [showAll, loadAll]);

  const shiftMonth = (dir) => {
    let m = month + dir, y = year;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y);
  };

  const myData      = data || {};
  const mySessions  = myData.sessions || [];
  const hoursUsed   = myData.hours_used ?? 0;
  const maxHours    = myData.max_hours  ?? 40;
  const hoursRem    = myData.hours_remaining ?? maxHours;
  const planStatus  = myData.plan_status || allData?.plan_status;
  const approvedAt  = myData.approved_at;

  // Which sessions to show on calendar / list
  const displaySessions = showAll
    ? (allData?.assignments || []).map(a => ({ ...a, display_status: planStatus === "approved" ? "approved" : a.status }))
    : mySessions;

  const kpis = [
    { label: "My Sessions", value: mySessions.length, icon: <FaCalendarAlt />, color: "#6366f1" },
    { label: "Approved",    value: mySessions.filter(s => s.display_status === "approved").length, icon: <FaCheckCircle />, color: "#10b981" },
    { label: "Hours Used",  value: `${hoursUsed}h`, icon: <FaClock />, color: "#f59e0b" },
    { label: "Hrs Left",    value: `${hoursRem}h`,  icon: <FaHourglassHalf />, color: hoursRem > 0 ? getBrandColors().green : "#ef4444" },
  ];

  return (
    <div style={{ fontFamily: FONT, maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <PageHeaderIcon icon={<FaCalendarAlt size={20} color={getBrandColors().blueDark} />} />
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: T.text, letterSpacing: "-0.5px" }}>My Coaching Schedule</h1>
          <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>
            {currentUser?.full_name ? `Welcome, ${currentUser.full_name.split(" ")[0]}! ` : ""}
            View your assigned sessions and available hours for the month.
          </div>
        </div>
      </div>

      {/* Month nav + controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <button style={S.gBtn} onClick={() => shiftMonth(-1)}><FaChevronLeft /></button>
        <span style={{ fontSize: 16, fontWeight: 800, color: T.text, fontFamily: FONT, minWidth: 160, textAlign: "center" }}>
          {MONTH_NAMES[month-1]} {year}
        </span>
        <button style={S.gBtn} onClick={() => shiftMonth(1)}><FaChevronRight /></button>

        {/* Show All Coaches checkbox */}
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none",
          padding: "7px 14px", borderRadius: 8, border: showAll ? `1.5px solid ${T.yellow}` : `1.5px solid ${T.inputBdr}`,
          background: showAll ? `rgba(247,206,11,0.10)` : T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
          fontSize: 13, fontWeight: 700, color: showAll ? T.blue : T.textLabel, fontFamily: FONT, transition: "all 0.15s" }}>
          <input
            type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)}
            style={{ width: 15, height: 15, accentColor: T.yellow, cursor: "pointer" }}
          />
          <FaUserTie size={11} /> Show All Coaches
        </label>

        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {["calendar","list"].map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer",
              fontFamily: FONT, display: "flex", alignItems: "center", gap: 5,
              background: view === v ? `linear-gradient(135deg,${T.yellow},#fff3a0)` : T.glass,
              border: view === v ? "1.5px solid rgba(255,200,0,0.45)" : T.glassBdr,
              color: view === v ? getBrandColors().blueDark : T.textLabel,
              boxShadow: view === v ? "0 4px 14px rgba(255,200,0,0.28)" : T.glassSh,
              backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
            }}>
              {v === "calendar" ? <FaCalendarAlt size={11} /> : <FaLayerGroup size={11} />}
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Plan approval banner */}
      {planStatus === "approved" && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderRadius: 12, marginBottom: 16,
          background: "rgba(16,185,129,0.10)", border: "1.5px solid rgba(16,185,129,0.30)" }}>
          <FaCheckCircle size={18} style={{ color: "#10b981", flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: getBrandColors().green, fontFamily: FONT }}>Schedule Approved &amp; Released</div>
            {approvedAt && (
              <div style={{ fontSize: 11, color: getBrandColors().green, opacity: 0.8, fontFamily: FONT, marginTop: 2 }}>
                Approved on {fmtDate(approvedAt)}
              </div>
            )}
          </div>
        </div>
      )}
      {planStatus === "draft" && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", borderRadius: 12, marginBottom: 16,
          background: "rgba(251,191,36,0.10)", border: "1.5px solid rgba(251,191,36,0.30)" }}>
          <FaHourglassHalf size={16} style={{ color: getBrandColors().orange, flexShrink: 0 }} />
          <div style={{ fontSize: 13, fontWeight: 700, color: getBrandColors().orange, fontFamily: FONT }}>Draft — pending admin approval</div>
        </div>
      )}

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 20 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR, border: T.glassBdr, borderRadius: 14, padding: "16px 18px", textAlign: "center" }}>
            <div style={{ fontSize: 20, color: k.color, marginBottom: 6 }}>{k.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: FONT }}>{k.value}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: FONT }}>{k.label}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ color: T.textMuted, fontSize: 13, fontFamily: FONT }}>Loading…</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: view === "calendar" ? "1fr 260px" : "1fr", gap: 20 }}>
          {/* Main content */}
          <div style={S.card}>
            {showAll && allData === null && (
              <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, marginBottom: 12 }}>Loading all coaches…</div>
            )}
            {view === "calendar"
              ? <ScheduleCalendar month={month} year={year} sessions={displaySessions} showAll={showAll} planStatus={planStatus} T={T} />
              : <SessionList sessions={displaySessions} showAll={showAll} planStatus={planStatus} T={T} />
            }
          </div>

          {/* Sidebar (calendar view only) */}
          {view === "calendar" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={S.card}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 16, fontFamily: FONT }}>
                  <FaClock style={{ marginRight: 8, color: T.yellow }} />My Hours Overview
                </div>
                <HoursGauge used={hoursUsed} max={maxHours} T={T} />
              </div>

              <div style={S.card}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12, fontFamily: FONT }}>Upcoming Sessions</div>
                {mySessions
                  .filter(s => s.assign_date >= new Date().toISOString().slice(0,10))
                  .slice(0, 6)
                  .map(s => {
                    return (
                      <div key={s.id} style={{ paddingBottom: 10, marginBottom: 10, borderBottom: `1px solid ${T.divider}` }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: FONT }}>{s.batch_name}</div>
                        <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT }}>
                          {fmtDate(s.assign_date)}
                        </div>
                      </div>
                    );
                  })}
                {mySessions.filter(s => s.assign_date >= new Date().toISOString().slice(0,10)).length === 0 && (
                  <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT }}>No upcoming sessions</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
